import type { ThemePreference } from "../../lib/theme";
import { useTheme } from "../../lib/theme";

interface ThemeOption {
  value: ThemePreference;
  /** One word, because three words have to be read at a glance. */
  label: string;
}

/** Follow the machine first; the two overrules come after it. */
const OPTIONS: ThemeOption[] = [
  { value: "system", label: "System" },
  { value: "light", label: "Light" },
  { value: "dark", label: "Dark" },
];

/**
 * Choose the console's palette: follow this machine, or pick one.
 *
 * It sits with the identity rather than on a settings screen, because it is a
 * preference of the person reading and not a property of what they are
 * reading. Real radios in a named group: one is always on, arrow keys move
 * between them, and the choice survives the next visit.
 *
 * Returns:
 *     The three-way theme control.
 */
export function ThemeSwitch(): JSX.Element {
  const { preference, setPreference } = useTheme();

  return (
    <div className="theme-switch">
      <span className="theme-switch__label">Theme</span>
      <fieldset className="theme-switch__options">
        <legend className="sr-only">Theme</legend>
        {OPTIONS.map((option) => (
          <label
            key={option.value}
            className="theme-switch__option"
            data-selected={preference === option.value}
          >
            <input
              type="radio"
              name="theme"
              className="sr-only"
              value={option.value}
              checked={preference === option.value}
              onChange={() => setPreference(option.value)}
            />
            {option.label}
          </label>
        ))}
      </fieldset>
    </div>
  );
}
